import { useState } from 'react';

import { clockTime, shortDate } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { CodeView } from '../../shared/ui/code-view';
import { DataPanel, Section } from '../../shared/ui/layout';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { OptionCard } from '../../shared/ui/option-card';
import { Switch } from '../../shared/ui/switch';
import { InfoDot } from '../../shared/ui/tooltip';
import { generateSecret, SIGNATURE_HEADER, VERIFY_SNIPPET } from '../../entities/destination';
import type { TDestinationSettings } from '../destination-settings';

const formatDateTime = (iso: string, timeZone?: string) =>
  `${shortDate(iso, timeZone)}, ${clockTime(iso, timeZone)}`;

const Label = ({ children }: { children: string }) => (
  <span className="mb-label block text-micro font-medium tracking-group text-faint">
    {children}
  </span>
);

export const DestinationSigning = ({
  settings,
  timeZone,
}: {
  settings: TDestinationSettings;
  timeZone?: string;
}) => {
  const { draft, change, destination } = settings;
  const [replacing, setReplacing] = useState(false);
  const [typed, setTyped] = useState('');
  const [confirmOff, setConfirmOff] = useState(false);

  const name = destination.name;
  const stored = destination.secretSet && draft.secret === undefined;
  const pending = typeof draft.secret === 'string' && draft.secret.length > 0;
  const hasSecret = stored || pending;
  const authenticated = draft.authType !== 'none';

  const secretLine = pending
    ? 'A new key — in use when you save'
    : stored
      ? destination.signingSecretSetAt
        ? `Set ${formatDateTime(destination.signingSecretSetAt, timeZone)}. Never shown again`
        : 'Set. It is never shown again'
      : 'No key yet — nothing is signed until there is one';

  const turnOff = () => {
    // `secret: ''` deletes it; omitting the field would leave a stored key that signs nothing.
    change({ signOutbound: false, secret: '' });
    setConfirmOff(false);
  };

  const unsigned = authenticated
    ? `${name} knows it is us by the credential under “How we call it”, but cannot check the body was not altered on the way.`
    : `${name} takes our word for it. Anyone who learns the URL can post to it as us.`;

  return (
    <Section
      title="Signing"
      description="Separate from the endpoint credential: turning this off or replacing the key never touches it."
    >
      <OptionCard
        className="max-w-form-narrow"
        selected={draft.signOutbound}
        control={
          <Switch
            checked={draft.signOutbound}
            aria-label="Sign what we send"
            onCheckedChange={(next) => {
              if (!next && hasSecret) return setConfirmOff(true);
              change({ signOutbound: next, ...(next ? {} : { secret: '' }) });
            }}
          />
        }
        title={
          <span className="flex items-center gap-2">
            Sign what we send
            <InfoDot>
              Turning this off deletes the key. A key that signs nothing is not kept.
            </InfoDot>
          </span>
        }
        description={
          draft.signOutbound
            ? `${name} can then check the request came from us and was not altered.`
            : unsigned
        }
      >
        {draft.signOutbound ? (
          <>
            <span className="block">
              <Label>Signing key</Label>
              <span className="flex gap-2">
                <Input
                  disabled
                  mono
                  aria-label="Signing key"
                  className="min-w-0 grow"
                  value={secretLine}
                  readOnly
                />
                <Button variant="ghost" size="md" onClick={() => setReplacing(true)}>
                  {hasSecret ? 'Replace' : 'Add a key'}
                </Button>
              </span>
              {destination.previousSecretExpiresAt && (
                <span className="mt-1 block text-meta text-muted">
                  The previous key still signs alongside it until{' '}
                  {formatDateTime(destination.previousSecretExpiresAt, timeZone)}.
                </span>
              )}
            </span>

            <span className="mt-4 block">
              <Label>{`What ${name} receives`}</Label>
              <DataPanel className="py-inset">
                <span className="block font-mono text-meta text-primary">
                  {SIGNATURE_HEADER}: t=&lt;unix seconds&gt;,v1=&lt;hex&gt;
                </span>
                <span className="mt-1 block font-mono text-meta text-muted">
                  v1 = hex(HMAC-SHA256(key, t + &quot;.&quot; + raw body))
                </span>
                <span className="mt-1 block text-meta text-muted">
                  During a rotation there are two v1 values; either one matching is a pass. Reject a
                  t more than five minutes from now — that is what stops a replay.
                </span>
              </DataPanel>
            </span>

            <span className="mt-4 block">
              <Label>Checking it, in Node</Label>
              <CodeView value={VERIFY_SNIPPET} language="javascript" />
            </span>
          </>
        ) : null}
      </OptionCard>

      <Modal
        open={replacing}
        onOpenChange={(open) => {
          setReplacing(open);
          if (!open) setTyped('');
        }}
        width="md"
        title={hasSecret ? 'Replace the signing key' : 'Add a signing key'}
        description={
          hasSecret
            ? `From the save on, ${name} gets both signatures for 24 hours, then only the new one.`
            : `Everything we send to ${name} is signed with it from the save on.`
        }
        submit={{
          label: 'Keep it',
          onSubmit: () => {
            change({ secret: typed.trim() });
            setReplacing(false);
            setTyped('');
          },
          disabled: typed.trim().length === 0,
        }}
      >
        <span className="flex gap-2">
          <Input
            mono
            autoFocus
            aria-label="New signing key"
            placeholder="whsec_…"
            className="min-w-0 grow"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
          <Button variant="ghost" size="md" onClick={() => setTyped(generateSecret())}>
            Generate
          </Button>
        </span>
        <Note className="mt-3">
          Copy it to {name} before you save: it is stored encrypted and never shown again — not
          here, not in an event, not in the reachability check.
        </Note>
      </Modal>

      <Modal
        open={confirmOff}
        onOpenChange={setConfirmOff}
        title="Turning signing off deletes the key"
        description={unsigned}
        cancelLabel="Keep signing on"
        submit={{ label: 'Turn it off and delete the key', onSubmit: turnOff }}
      >
        <Note tone="danger">
          There is no copy of it anywhere — turning signing back on means a new key.
          {authenticated && ' The endpoint credential is not affected.'}
        </Note>
      </Modal>
    </Section>
  );
};
